import { apiFetch } from './apiFetch';
import type {
  BankDepositDetail,
  BankDepositSummary,
  CashRegister,
  CashRegisterUpsert,
  ClosingDetail,
  ClosingSummary,
  CreateBankDepositRequest,
  CreateClosingRequest,
  ListBankDepositsQuery,
  ListClosingsQuery,
  RegisterCurrent,
  SafeState,
} from '@deposits/shared';

// ───────────────────────────── Cash registers ──────────────────────────────

export async function fetchCashRegisters(
  locationId?: number,
  projectId?: number | null,
): Promise<CashRegister[]> {
  const params = new URLSearchParams();
  if (locationId != null) params.set('location_id', String(locationId));
  if (projectId != null) params.set('project_id', String(projectId));
  const qs = params.toString();
  return apiFetch<CashRegister[]>(`/cash-registers${qs ? `?${qs}` : ''}`);
}

export async function fetchRegisterCurrent(id: number): Promise<RegisterCurrent> {
  return apiFetch<RegisterCurrent>(`/cash-registers/${id}/current`);
}

export async function createCashRegister(body: CashRegisterUpsert): Promise<CashRegister> {
  return apiFetch<CashRegister>('/cash-registers', { method: 'POST', body: JSON.stringify(body) });
}

export async function updateCashRegister(id: number, body: CashRegisterUpsert): Promise<CashRegister> {
  return apiFetch<CashRegister>(`/cash-registers/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

export async function deleteCashRegister(id: number): Promise<void> {
  await apiFetch<void>(`/cash-registers/${id}`, { method: 'DELETE' });
}

// ─────────────────────────── Register closings ──────────────────────────────

export interface CreateClosingResponse {
  id: number;
  closing_number: number;
  amount_to_safe: number;
  variance: number;
  pdf_status: 'pending' | 'ready' | 'failed';
  pdf_url: string | null;
}

export async function fetchClosings(q: ListClosingsQuery): Promise<ClosingSummary[]> {
  const params = new URLSearchParams({
    location_id: String(q.location_id),
    from: q.from,
    to: q.to,
    status: q.status,
  });
  if (q.project_id != null) params.set('project_id', String(q.project_id));
  return apiFetch<ClosingSummary[]>(`/register-closings?${params.toString()}`);
}

export async function fetchClosingDetail(id: number): Promise<ClosingDetail> {
  return apiFetch<ClosingDetail>(`/register-closings/${id}`);
}

export async function createClosing(body: CreateClosingRequest): Promise<CreateClosingResponse> {
  return apiFetch<CreateClosingResponse>('/register-closings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function cancelClosing(id: number, reason?: string | null): Promise<void> {
  await apiFetch<void>(`/register-closings/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

// ─────────────────────────── Bank deposits (safe) ───────────────────────────

export interface CreateBankDepositResponse {
  id: number;
  deposit_number: number;
  total: number;
  pdf_status: 'pending' | 'ready' | 'failed';
  pdf_url: string | null;
}

export async function fetchSafe(locationId: number, projectId: number | null): Promise<SafeState> {
  const params = new URLSearchParams({ location_id: String(locationId) });
  if (projectId != null) params.set('project_id', String(projectId));
  return apiFetch<SafeState>(`/bank-deposits/safe?${params.toString()}`);
}

export async function fetchBankDeposits(q: ListBankDepositsQuery): Promise<BankDepositSummary[]> {
  const params = new URLSearchParams({
    location_id: String(q.location_id),
    from: q.from,
    to: q.to,
    status: q.status,
  });
  if (q.project_id != null) params.set('project_id', String(q.project_id));
  return apiFetch<BankDepositSummary[]>(`/bank-deposits?${params.toString()}`);
}

export async function fetchBankDepositDetail(id: number): Promise<BankDepositDetail> {
  return apiFetch<BankDepositDetail>(`/bank-deposits/${id}`);
}

export async function createBankDeposit(
  body: CreateBankDepositRequest,
): Promise<CreateBankDepositResponse> {
  return apiFetch<CreateBankDepositResponse>('/bank-deposits', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function cancelBankDeposit(id: number, reason?: string | null): Promise<void> {
  await apiFetch<void>(`/bank-deposits/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}
